import { Component, Input, OnDestroy, OnInit } from '@angular/core';

// Material
import { MatDialog } from '@angular/material/dialog';

// Root Singleton Services
import { ApiService, HttpClientService } from 'app/core/services';

// ngRx
import { select, Store } from '@ngrx/store';
import * as fromRoot from 'app/core/store';
import { Observable, Subscription } from 'rxjs';
import * as fromAuth from '../../../../auth/store';
import { environment } from '../../../../../environments/environment.prod';

@Component({
  selector: 'ebo-webchat',
  templateUrl: './webchat.component.html',
  styleUrls: ['./webchat.component.scss']
})
export class WebchatComponent implements OnInit, OnDestroy {
  @Input() userChatId: string;
  @Input() chatUsername: string;
  env$: Observable<string>;
  botId$: Observable<number>;
  envSub: Subscription;
  env: string;
  botIdSub: Subscription;
  isScriptLoaded = false;

  private networkPatched = false;
  private originalFetch: typeof window.fetch;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open;
  private originalSetAttribute: typeof Element.prototype.setAttribute;
  private originalScriptSrcDescriptor: PropertyDescriptor;
  private readonly directConfigHosts: { [clientName: string]: string } = {
    bovey: 'app-bovey-stdprod-gwc-002.azurewebsites.net'
  };

  constructor(
    public dialog: MatDialog,
    private store: Store<fromRoot.AppState>,
    private http: HttpClientService,
    private apiService: ApiService
  ) {
    this.env$ = this.store.pipe(select(fromAuth.getEnvironment));
    this.botId$ = this.store.pipe(select(fromAuth.getBotId));
    this.envSub = this.env$.subscribe(env => {
      if (env !== this.env) {
        this.env = env;
      }
    });
  }

  // Redirects the webchat widget's config API calls to the direct Azure host when
  // the ebo.ai custom domain (e.g. bovey-configuration.ebo.ai) has no DNS entry.
  private patchNetworkForDirectAccess(clientName: string): void {
    clientName = clientName && clientName.toLowerCase();

    let apiHost: string;
    try {
      apiHost = this.directConfigHosts[clientName] || new URL(this.apiService.apiUrl).hostname;
    } catch {
      return;
    }
    const eboConfigHost = `${clientName}-configuration.ebo.ai`;
    if (apiHost === eboConfigHost) return; // ebo.ai DNS is in place — no patch needed

    const replaceConfigHost = (url: string): string => url.includes(eboConfigHost) ? url.split(eboConfigHost).join(apiHost) : url;

    this.originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      if (typeof input === 'string') {
        input = replaceConfigHost(input);
      } else if (input instanceof URL) {
        input = new URL(replaceConfigHost(input.toString()));
      } else if (typeof Request !== 'undefined' && input instanceof Request && input.url.includes(eboConfigHost)) {
        input = new Request(replaceConfigHost(input.url), input);
      }
      return this.originalFetch(input as RequestInfo, init);
    };

    const origOpen = XMLHttpRequest.prototype.open;
    this.originalXHROpen = origOpen;
    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: any[]) {
      if (typeof url === 'string') {
        url = replaceConfigHost(url);
      } else if (url instanceof URL) {
        url = new URL(replaceConfigHost(url.toString()));
      }
      return origOpen.call(this, method, url, ...rest);
    };

    this.originalSetAttribute = Element.prototype.setAttribute;
    const originalSetAttribute = this.originalSetAttribute;
    Element.prototype.setAttribute = function (name: string, value: string): void {
      const attributeName = name && name.toLowerCase();
      const attributeValue = typeof value === 'string' && (attributeName === 'src' || attributeName === 'href')
        ? replaceConfigHost(value)
        : value;

      return originalSetAttribute.call(this, name, attributeValue);
    };

    this.originalScriptSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
    if (this.originalScriptSrcDescriptor && this.originalScriptSrcDescriptor.set) {
      const originalScriptSrcDescriptor = this.originalScriptSrcDescriptor;
      Object.defineProperty(HTMLScriptElement.prototype, 'src', {
        ...originalScriptSrcDescriptor,
        set(value: string) {
          originalScriptSrcDescriptor.set.call(this, replaceConfigHost(value));
        }
      });
    }

    this.networkPatched = true;
  }

  private restoreNetwork(): void {
    if (this.networkPatched) {
      window.fetch = this.originalFetch;
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      Element.prototype.setAttribute = this.originalSetAttribute;
      if (this.originalScriptSrcDescriptor) {
        Object.defineProperty(HTMLScriptElement.prototype, 'src', this.originalScriptSrcDescriptor);
      }
      this.networkPatched = false;
    }
  }

  public loadScript(): void {
    const body = document.body as HTMLDivElement;
    const script = document.createElement('script');
    script.src = `https://cdn.ebo.ai/webchat/ebo-chat-${environment.webchatFileName}.js`;
    script.id = 'webchat-widget';
    script.async = true;
    body.appendChild(script);
  }

  ngOnInit(): void {
    setTimeout(() => {
      const div = document.querySelector('#webchat-root');

      if (div) {
        div.setAttribute('data-env', this.env.toLowerCase());
        div.setAttribute(
          'data-name',
          this.apiService.conversationApiUrl
            .replace('https://', '')
            .replace('-conversation.ebo.ai', '')
            .replace('-conversation-core.ebo.ai', '')
            .replace(/^app-([^-]+)-.*\.azurewebsites\.net/, '$1')
            .replace('/', '')
        );
        div.setAttribute('data-userid', this.userChatId);
        div.setAttribute('data-chatusername', this.chatUsername);

        this.botIdSub = this.botId$.subscribe(id => {
          if (id) {
            div.setAttribute('data-botid', String(id));
          }
        });

        const clientName = div.getAttribute('data-name');
        this.patchNetworkForDirectAccess(clientName);
        this.loadScript();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('virtualAgentToken');
    sessionStorage.removeItem('virtualAgentSession');
    sessionStorage.removeItem('welcomeMessageExecuted');
    sessionStorage.removeItem('virtualAgentState');
    sessionStorage.removeItem('virtualAgentTime');

    const getScript = document.querySelector('#webchat-widget');

    if (getScript) {
      getScript.remove();
    }
    const tokenObj = JSON.parse(sessionStorage.getItem('virtualAgentToken'));

    this.restoreNetwork();

    if (this.envSub && this.botIdSub) {
      this.envSub.unsubscribe();
      this.botIdSub.unsubscribe();
      this.isScriptLoaded = false;
    }

    if (tokenObj) {
      const base64Url = tokenObj.token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      const obj = JSON.parse(jsonPayload);
      this.http.get(`${this.apiService.conversationApiUrl}conversations/deactivate?ConversationId=${obj.conv}`, true).subscribe();
    }
  }
}
